import * as React from 'react';
import {TextEdit} from '../../../text-edit';
import {AffectedFamilyPageProps} from './affected-family-page.types';
import {useAffectedFamilyPageController} from './affected-family-page.controller';
import {I18n} from 'react-redux-i18n';
import {Flex} from '@common/components/Flex';
import {Title} from '../../../title';
import {Button} from '@common/components/Button';
import {BackBtn} from '../../../back-btn';
import {FieldSelector} from '@common/components/FieldSelector';
import {Field} from '../field';
import {FieldProperty} from '../field/field.types';
import {Loading} from '@common/components/loading';
import {FieldSelectorGetFieldParams} from '@common/components/FieldSelector/field-selector.types';
import {NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES} from '@modules/fhq-design-tool/constants/page';
import {QuestionnaireTextBlockLocalDto} from '@modules/fhq-design-tool/dto/text-block-create-dto';
import {Subtitle} from '../../../subtitle';
import {TextEditor} from '../../../text-editor';
import {TextBlock} from '../text-block';
import {FieldsWithTextBlocks} from '../../../fields-with-text-blocks';
import {FieldInList} from '../../../fields-with-text-blocks/fields-with-text-blocks.types';

import './affected-family-page.styles.scss';

export const AffectedFamilyPage: React.FC<AffectedFamilyPageProps> = (props) => {
  const { cachedData, showConfirmMessage, onGoBack, getFields, getFolders, scrollParentToTop, errors } = props;

  const {
    editingTextBlock,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    canChangeFieldOrder,
    canDeleteField,
    isFirstNameField, 
    loadingPage,
    handleFieldChange,
    goBackFromFieldEdit,
    editingField,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    navigateOnFieldEdit,
    canShowHideField,
    handlePageNameChange,
    handlePageDescriptionChange,
    fieldHasError,
    handleFieldsOrTextBlocksChange,
    handleAddTextBlock,
    handleFieldAdd,
    combinedItems
  } = useAffectedFamilyPageController(props);

  if (editingField) {
    return (
      <Field
        errors={errors?.fields ? errors?.fields[editingField.progenyId] : undefined}
        editableProperties={isFirstNameField(editingField) ? [FieldProperty.name, FieldProperty.required, FieldProperty.readonly] : undefined}
        scrollParentToTop={scrollParentToTop}
        getFieldsForSkipLogic={getFields}
        getFoldersForSkipLogic={getFolders}
        data={editingField}
        onGoBack={goBackFromFieldEdit}
        onChange={handleFieldChange}
        showConfirmMessage={showConfirmMessage}
      />
    );
  }

  if (editingTextBlock) {
    return (
      <TextBlock 
        data={editingTextBlock}
        onGoBack={closeTextBlockEditing}
        getFieldsForSkipLogic={getFields}
        getFoldersForSkipLogic={getFolders}
        onChange={handleTextBlockChange}
        errors={errors?.texts?.[editingTextBlock.id]}
      />
    );
  }

  if (loadingPage || !cachedData) {
    return (
      <div className="FhqDesignToolQuestionnaireDesignerPage">
        <Loading size={20} margin="20px" />
      </div>
    );
  }

  return (
    <div className="FhqDesignToolQuestionnaireDesignerAffectedPage">
      <BackBtn className="FhqDesignToolQuestionnaireDesignerAffectedPage__back-btn" onClick={onGoBack} />
      <TextEdit 
        errors={errors?.properties?.title}
        required
        metadata={{
          description: 'page-name',
        }}
        value={cachedData.title}
        onChange={handlePageNameChange}
      />
      <Subtitle type="chip" text={I18n.t('Affected family members')} />
      <TextEditor
        metadata={{
          description: 'page-description',
        }}
        label={I18n.t('Page Description')}
        value={cachedData.description ?? ''}
        onChange={handlePageDescriptionChange}
      />
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireDesignerAffectedPage__add-item-header">
        <Title text={I18n.t('Fields')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireDesignerAffectedPage__add-item-btn" onClick={openFieldChooser}>
          +
        </Button>
      </Flex>
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireDesignerFamilyTablePage__add-item-header">
        <Title text={I18n.t('Text')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireDesignerFamilyTablePage__add-item-btn" onClick={handleAddTextBlock}>
          +
        </Button>
      </Flex>
      <FieldsWithTextBlocks<FieldInList, QuestionnaireTextBlockLocalDto>
        combinedItems={combinedItems}
        onChange={handleFieldsOrTextBlocksChange}
        fieldProps={{
          hasError: fieldHasError,
          onEdit: navigateOnFieldEdit, 
          showDeleteConfirmMessage: showConfirmMessage,
          canShowHideField, 
          canDeleteField,
          canChangeFieldOrder,
        }}
        textBlocksProps={{
          onEdit: openTextBlockEditing,
          showDeleteConfirmMessage: showConfirmMessage,
          hasError: x => Boolean(errors?.texts?.[x.id]),
        }}
      />
      {fieldChooserOpened && (
        <FieldSelector
          withSearch
          getFolders={getFolders}
          getFields={({ searchKeyword, ...restParams }: FieldSelectorGetFieldParams) => 
            getFields({ searchQuery: searchKeyword, ...restParams, includeTables: true })
              .then((fields) => 
                fields.filter((x) => !NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES.includes(x.fieldTypeId))
              )
          }
          onChange={handleFieldAdd}
          close={closeFieldChooser}
          closeOnChange={false}
          multiple
        />
      )}
    </div>
  );
};